import { useEffect, useMemo, useState } from 'react'
import { useEditorContext } from '@/scenes/engine'
import Download from '../Icons/Download'
import { useLocation, useParams } from 'react-router'
import { KIND } from 'baseui/button'
import { useSelector } from 'react-redux'
import useModal from '@/hooks/useModal'
import { selectOpenModalTryPremium, selectUserIsPremium } from '@/store/slices/user/selectors'
import { useAppDispatch } from '@/store/store'
import { customAmplitude } from '@/utils/customAmplitude'
import { NavbarButtonBaseProps } from './navbarConfig'
import { useTranslation } from 'react-i18next'
import { PLACEMENT, StatefulPopover } from 'baseui/popover'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import HeadingInspector from '@/components/HeadingInspector'
import Icons from '../Icons'
import { lightTheme } from '@/customTheme'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useDownloadConfetti } from '@/hooks/useDownloadConfetti'
import AutoScroll from '@/components/AutoScroll'
import { Block } from 'baseui/block'
import ScrollButtonsLeftRight from '@/components/ScrollButtonsLeftRight'
import { Z_INDEX } from '@/constants/z-index'
import FrameHandler from '@/scenes/engine/handlers/FrameHandler'
import emptyFramBG from '@assets/images/empty_frame_bg.svg'
import { FORMAT_OPTIONS, FormatOption } from '@/scenes/Editor/components/Navbar/components/downloadTypes'
import { buildPremiumPromptProperties, buildSignupAnalyticsContext } from '@/utils/signupPremiumAnalytics'


const enum DOWNLOAD_PAGE_OPTION {
  CURRENT = 'CURRENT',
  ALL = 'ALL',
}

const DOWNLOAD_LAYOUT = {
  modalWidth: 450,
  modalRadius: 16,
  headerHeight: 80,
  headerPaddingY: 24,
  footerHeight: 88,
  previewMaxHeight: 270,
  previewMinHeight: 142,
  previewHeightWithoutImage: 232,
  previewHeightMultiPageOffset: 216,
  previewHeightSinglePageOffset: 120,
  previewPadding: 24,
  previewMaxWidth: 270,
  badgeOffset: 8,
  badgeMinWidth: 52,
  badgeHeight: 21,
  badgeRadius: 100,
  badgePaddingY: 2,
  badgePaddingX: 10,
  contentPaddingX: 24,
  contentPaddingTop: 8,
  autoScrollOffsetPremium: 88,
  autoScrollOffsetFree: 152,
  modalWithoutPreviewHeight: 452,
  popoverWidth: 402,
  stickyViewportHeight: 594,
  footerPadding: 24,
  blockGap: 16,
  smallGap: 8,
}

function ExportImage({ disabled = false }: NavbarButtonBaseProps) {
  const { editor } = useEditorContext()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()

  useEffect(() => () => editor?.handlers.downloadHandler.cancelEditorDownload(), [editor])
  useEffect(() => {
    editor?.handlers.downloadHandler.validateEditorDownload()
  }, [editor, location.pathname])

  const [singlePageValueSelected, setSinglePageValueSelected] = useState<FormatOption>(FORMAT_OPTIONS['jpeg'])
  const [multipageValueSelected, setMultipageValueSelected] = useState<FormatOption>(FORMAT_OPTIONS['pdf'])
  const [downloadPageSelected, setDownloadPageSelected] = useState<DOWNLOAD_PAGE_OPTION>(DOWNLOAD_PAGE_OPTION.CURRENT)
  const [isOpenSelectPages, setIsOpenSelectPages] = useState(false)
  const [isOpenSaveFormat, setIsOpenSaveFormat] = useState(false)
  const buildPremiumPromptEventProperties = (source: string) =>
    buildPremiumPromptProperties(
      buildSignupAnalyticsContext({
        source,
        type: 'Standard',
        pathname: window.location.pathname,
      }),
    )
  
  // Use useMemo for reactive currentFormatValue
  const currentFormatValue = useMemo(() => {
    return downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL ? multipageValueSelected : singlePageValueSelected;
  }, [downloadPageSelected, multipageValueSelected, singlePageValueSelected]);
  
  const setCurrentFormatValue = (value: FormatOption) => {
    if (downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL) {
      setMultipageValueSelected(value);
    } else {
      setSinglePageValueSelected(value);
    }
  }

  const [previewPageActive, setPreviewPageActive] = useState(1)
  const [previews, setPreviews] = useState<string[]>([])

  const hasPremium = useSelector(selectUserIsPremium)

  const handleDisplayConfetti = useDownloadConfetti()

  const { showModalType, setShowModalCustomText } = useModal()

  const dispatch = useAppDispatch()
  const [isOpenPopover, setIsOpenPopover] = useState(false)
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)

  const currentPageIndex = Math.max(editor?.handlers.pagesHandler.getCurrentSceneIndex() + 1, 1)

  useEffect(() => {
    if (!editor) return
    const refreshDefaults = () => {
      setDownloadPageSelected(editor.handlers.pagesHandler.scenes.length > 1 ? DOWNLOAD_PAGE_OPTION.ALL : DOWNLOAD_PAGE_OPTION.CURRENT)
      setSinglePageValueSelected(editor.handlers.downloadHandler.getDefaultFormat('current'))
      setMultipageValueSelected(editor.handlers.downloadHandler.getDefaultFormat('all'))
    }
    refreshDefaults()
    editor.on('frame:transparent', refreshDefaults)
    editor.on('scenes:changed', refreshDefaults)
    return () => {
      editor.off('frame:transparent', refreshDefaults)
      editor.off('scenes:changed', refreshDefaults)
    }
  }, [editor, hasPremium])

  useEffect(() => {
    if(!editor) return

    const getPreviewMultiplier = () => {
      if (!editor) return 1;
      const previewSize = 300 * 2
      return previewSize / editor.handlers.frameHandler.getSize().height
    }

    const fetchPreviews = async () => {
      const scenesPreviews = Object.values(await editor.handlers.pagesHandler.getScenesPreview(getPreviewMultiplier()))
      setPreviews(scenesPreviews)
      setPreviewPageActive(currentPageIndex)
    }

    if(isOpenPopover) {
      fetchPreviews()
    }
  }, [isOpenPopover, editor, currentPageIndex])

  const createAndDownloadImage = async () => {
    await editor.handlers.downloadHandler.download({
      scope: [editor.handlers.pagesHandler.currentScene.sceneId],
      format: currentFormatValue,
      quality: 1,
      routeId: id,
      source: 'Editor',
    })
  }

  const { t } = useTranslation()

  const downloadFromPreviewModal = async () => {
    const scope = downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL
      ? 'all' as const
      : [editor.handlers.pagesHandler.scenes[previewPageActive - 1].sceneId]
    const result = await editor.handlers.downloadHandler.downloadFromEditor({
      scope,
      format: currentFormatValue,
      quality: currentFormatValue.title === 'pdf' ? 0.92 : 1,
      routeId: id,
      source: 'Editor',
    })
    if (result.completed && !result.prompted) handleDisplayConfetti()
  }

  const frameSizeActive = editor?.handlers.frameHandler.getFrameDimensionsInUnit() || FrameHandler.DEFAULT_FRAME_DIMENSIONS
  const scenesCount = editor?.handlers.pagesHandler.scenes.length || 1
  const previewReservedHeight =
    DOWNLOAD_LAYOUT.previewHeightWithoutImage +
    (scenesCount > 1 ? DOWNLOAD_LAYOUT.previewHeightMultiPageOffset : DOWNLOAD_LAYOUT.previewHeightSinglePageOffset)
  const previewContainerHeight = `min(${DOWNLOAD_LAYOUT.previewMaxHeight}px, max(calc(100vh - ${previewReservedHeight}px), ${DOWNLOAD_LAYOUT.previewMinHeight}px))`
  const scrollAreaMaxHeight = `calc(100vh - ${DOWNLOAD_LAYOUT.headerHeight}px - ${hasPremium ? DOWNLOAD_LAYOUT.autoScrollOffsetPremium : DOWNLOAD_LAYOUT.autoScrollOffsetFree}px)`
  const frameRatio = frameSizeActive.size.width / frameSizeActive.size.height
  
  return (
    <StatefulPopover
      content={({ close }) => (
        <div
          style={{
            width: `${DOWNLOAD_LAYOUT.modalWidth}px`,
            background: '#fff',
            borderRadius: `${DOWNLOAD_LAYOUT.modalRadius}px`,
            height: 'fit-content',
            maxHeight: '100vh'
          }}
        >
          <HeadingInspector
            title={t('Download')}
            hasNavigation={false}
            hasClose={true}
            handleClose={close}
            style={{ height: `${DOWNLOAD_LAYOUT.headerHeight}px`, paddingTop: `${DOWNLOAD_LAYOUT.headerPaddingY}px`, paddingBottom: `${DOWNLOAD_LAYOUT.headerPaddingY}px` }}
          />
          <AutoScroll
            style={{
              height: 'fit-content',
              maxHeight: scrollAreaMaxHeight,
              padding: 0,
              margin: 0,
              overflowY: 'auto',
            }}
          >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              height: previewContainerHeight, // limits preview image height responsively
              background: lightTheme.colors.grayScale20,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: `${DOWNLOAD_LAYOUT.previewPadding}px`, paddingBottom: `${DOWNLOAD_LAYOUT.previewPadding}px`, width: `min(${DOWNLOAD_LAYOUT.previewMaxWidth}px, 100%)`, height: '100%', maxHeight: '100%', boxSizing: 'border-box'}}>
              <div style={{flexShrink: 0,position: 'relative', boxSizing: 'border-box', ...(frameRatio > 1 ? { height: 'fit-content', maxWidth: '100%' } : { height: '100%', width: 'fit-content' })}} >
                <img 
                  style={{ 
                    objectFit: 'contain', 
                    ...(frameRatio > 1 ? { height: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' } : { height: '100%', width: 'auto', objectFit: 'contain', display: 'block' }),
                    borderRadius: '8px', 
                    boxShadow: `0px 4px 12px 0px ${lightTheme.colors.primaryBoxShadow}`,
                    backgroundImage: `url(${emptyFramBG})`,
                  }} 
                  alt="" 
                  src={previews[previewPageActive - 1] || (editor?.handlers.pagesHandler.scenes[previewPageActive - 1]?.preview ?? '')} 
                />
                <div
                  style={{
                    position: 'absolute',
                    left: `${DOWNLOAD_LAYOUT.badgeOffset}px`,
                    top: `${DOWNLOAD_LAYOUT.badgeOffset}px`,
                    background: lightTheme.colors.blackGrayWith75Alpha,
                    minWidth: `${DOWNLOAD_LAYOUT.badgeMinWidth}px`,
                    maxWidth: 'fit-content',
                    height: `${DOWNLOAD_LAYOUT.badgeHeight}px`,
                    borderRadius: `${DOWNLOAD_LAYOUT.badgeRadius}px`,
                    ...lightTheme.typography.Small12medium,
                    lineHeight: '17px',
                    color: lightTheme.colors.white,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: `${DOWNLOAD_LAYOUT.badgePaddingY}px ${DOWNLOAD_LAYOUT.badgePaddingX}px`
                  }}
                  >
                  {t('%d of %d', { arg1: previewPageActive, arg2: editor?.handlers.pagesHandler.scenes.length })}
                </div>
              </div>
            </div>
            {editor.handlers.pagesHandler.scenes.length > 1 ? (
              <>
                <ScrollButtonsLeftRight 
                  handleLeft={()=>{
                    if(previewPageActive === 1) return
                    setPreviewPageActive(previewPageActive - 1)
                  }} 
                  handleRight={()=>{
                    if(previewPageActive === editor.handlers.pagesHandler.scenes.length) return
                    setPreviewPageActive(previewPageActive + 1)
                  }}
                  showLeft={previewPageActive > 1}
                  showRight={previewPageActive < editor.handlers.pagesHandler.scenes.length}/>
              </>
            ) : null}
          </div>
          <div style={{ padding: `${DOWNLOAD_LAYOUT.contentPaddingTop}px ${DOWNLOAD_LAYOUT.contentPaddingX}px 0` }}>
            <p
              style={{
                ...lightTheme.typography.Small11medium,
                color: lightTheme.colors.contentPrimary,
                textAlign: 'center',
                marginBottom: `${DOWNLOAD_LAYOUT.blockGap}px`
              }}
              >
              {/* @ts-ignore */}
              {t('%d X %d %d', { arg1: frameSizeActive.size.width, arg2: frameSizeActive.size.height, arg3: frameSizeActive.unit })}
            </p>
            {/* select pages */}
            {editor.handlers.pagesHandler.scenes.length > 1 ? (
              <>
                <p
                  style={{
                    ...lightTheme.typography.Small14Bold,
                    color: lightTheme.colors.blackGray,
                    margin: `0 0 ${DOWNLOAD_LAYOUT.smallGap}px 0`,
                  }}
                >
                  {t('Select pages')}
                </p>
                <StatefulPopover
                  ignoreBoundary
                  placement={PLACEMENT.bottom}
                  overrides={{
                    Body: {
                      style: ({ $theme }) => ({
                        marginTop: '4px',
                        zIndex: Z_INDEX.DOWNLOAD_MODAL,
                        transitionDuration: 'none',
                      }),
                    },
                    Inner: {
                      style: ({ $theme }) => ({
                        transitionDuration: 'none',
                      }),
                    },
                  }}
                  content={({ close: closeOptions }) => (
                    <AutoScroll
                      style={{
                        padding: `${DOWNLOAD_LAYOUT.blockGap}px ${DOWNLOAD_LAYOUT.smallGap}px ${DOWNLOAD_LAYOUT.blockGap}px ${DOWNLOAD_LAYOUT.blockGap}px`,
                        borderRadius: `${DOWNLOAD_LAYOUT.modalRadius}px`,
                        width: `${DOWNLOAD_LAYOUT.popoverWidth}px`,
                        background: '#fff',
                      }}
                      bottomMarginToContent
                    >
                      <Block
                        onClick={() => {
                          setDownloadPageSelected(DOWNLOAD_PAGE_OPTION.ALL)
                          closeOptions()
                        }}
                        $style={{
                          height: 'wrap-content',
                          cursor: 'pointer',
                          padding: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          borderRadius: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          display: 'flex ',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          gap: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          position: 'relative !important',
                          ':hover': {
                            background: '#F2F2F2',
                          },
                          ':active': {
                            background: lightTheme.colors.grayScale100,
                          },
                        }}
                        key={DOWNLOAD_PAGE_OPTION.ALL}
                      >
                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                          <p
                            style={{
                              ...lightTheme.typography.Small14regular,
                              margin: 0,
                            }}
                          >
                            {t('All pages (1-%d)', { arg1: editor.handlers.pagesHandler.scenes.length })}
                          </p>
                        </div>
                        {
                          downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL ? (
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                top: 0,
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                alignSelf: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Icons.CheckIcon fill="#FF0560" />
                            </div>
                          ) : null
                        }
                      </Block>
                      <Block
                        onClick={() => {
                          setDownloadPageSelected(DOWNLOAD_PAGE_OPTION.CURRENT)
                          closeOptions()
                        }}
                        $style={{
                          height: 'wrap-content',
                          cursor: 'pointer',
                          padding: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          borderRadius: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          display: 'flex ',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          gap: `${DOWNLOAD_LAYOUT.smallGap}px`,
                          position: 'relative !important',
                          ':hover': {
                            background: '#F2F2F2',
                          },
                          ':active': {
                            background: lightTheme.colors.grayScale100,
                          },
                        }}
                        key={DOWNLOAD_PAGE_OPTION.CURRENT}
                      >
                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                          <p
                            style={{
                              ...lightTheme.typography.Small14regular,
                              margin: 0,
                            }}
                          >
                            {t('Current page (page %d)', { arg1: previewPageActive })}
                          </p>
                        </div>
                        {
                          downloadPageSelected === DOWNLOAD_PAGE_OPTION.CURRENT ? (
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                top: 0,
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                alignSelf: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Icons.CheckIcon fill="#FF0560" />
                            </div>
                          ) : null
                        }
                      </Block>
                    </AutoScroll>
                  )}
                  onOpen={() => setIsOpenSelectPages(true)}
                  onClose={() => setIsOpenSelectPages(false)}
                >
                  {isOpenSelectPages ? (
                      <div>
                        <ButtonCustom
                          endEnhancer={<Icons.Drop />}
                          type={SizeButton.LARGE}
                          kind={KIND.tertiary}
                          style={{
                            ...lightTheme.typography.Small14regular,
                            fontWeight: 400,
                            justifyContent: 'space-between',
                            background: '#fff!important',
                            borderColor: '#B2B2B2!important',
                          }}
                        >
                          {downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL
                            ? t('All pages (1-%d)', { arg1: editor.handlers.pagesHandler.scenes.length })
                            : t('Current page (page %d)', { arg1: previewPageActive })}
                        </ButtonCustom>
                      </div>
                    ) : (
                      <ButtonCustom
                        endEnhancer={<Icons.Drop />}
                        type={SizeButton.LARGE}
                        kind={KIND.tertiary}
                        style={{
                          ...lightTheme.typography.Small14regular,
                          fontWeight: 400,
                          justifyContent: 'space-between',
                          background: '#fff!important',
                          ':hover': {
                            background: '#fff!important',
                            borderColor: '#B2B2B2!important',
                          },
                          ':active': {
                            background: '#fff!important',
                            borderColor: '#B2B2B2!important',
                          },
                        }}
                      >
                        {downloadPageSelected === DOWNLOAD_PAGE_OPTION.ALL
                          ? t('All pages (1-%d)', { arg1: editor.handlers.pagesHandler.scenes.length })
                          : t('Current page (page %d)', { arg1: previewPageActive })}
                      </ButtonCustom>
                    )}
                </StatefulPopover>
              </>
            ) : null}
            {/*  */}
            <p
              style={{
                ...lightTheme.typography.Small14Bold,
                color: lightTheme.colors.blackGray,
                margin: `${scenesCount > 1 ? DOWNLOAD_LAYOUT.blockGap : 0}px 0 ${DOWNLOAD_LAYOUT.smallGap}px 0`,
              }}
            >
              {t('Save format')}
            </p>
            <StatefulPopover
              ignoreBoundary
              placement={PLACEMENT.bottom}
              overrides={{
                Body: {
                  style: ({ $theme }) => ({
                    marginTop: '4px',
                    zIndex: Z_INDEX.DOWNLOAD_MODAL,
                    transitionDuration: 'none',
                  }),
                },
                Inner: {
                  style: ({ $theme }) => ({
                    transitionDuration: 'none',
                  }),
                },
              }}
              content={({ close: closeOptions }) => (
                <AutoScroll
                  style={{
                    padding: `${DOWNLOAD_LAYOUT.blockGap}px ${DOWNLOAD_LAYOUT.smallGap}px ${DOWNLOAD_LAYOUT.blockGap}px ${DOWNLOAD_LAYOUT.blockGap}px`,
                    borderRadius: `${DOWNLOAD_LAYOUT.modalRadius}px`,
                    width: `${DOWNLOAD_LAYOUT.popoverWidth}px`,
                    background: '#fff',
                  }}
                  bottomMarginToContent
                >
                  {Object.keys(FORMAT_OPTIONS).map((opt, index) => (
                    <Block
                      onClick={() => {
                        if (FORMAT_OPTIONS[opt].isPremium && !hasPremium) {
                          dispatch(setOpenModalTryPremium({
                          isOpen: true,
                          source: FORMAT_OPTIONS[opt].analyticSrc,
                          callback: () => setCurrentFormatValue(FORMAT_OPTIONS[opt]),
                          }));
                          customAmplitude(
                            'Premium Prompt',
                            buildPremiumPromptEventProperties(FORMAT_OPTIONS[opt].analyticSrc),
                          )
                          return
                        }
                        setCurrentFormatValue(FORMAT_OPTIONS[opt]);
                        closeOptions()
                      }}
                      $style={{
                        height: 'wrap-content',
                        cursor: 'pointer',
                        padding: `${DOWNLOAD_LAYOUT.smallGap}px`,
                        borderRadius: `${DOWNLOAD_LAYOUT.smallGap}px`,
                        display: 'flex ',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: `${DOWNLOAD_LAYOUT.smallGap}px`,
                        position: 'relative !important',
                        ':hover': {
                          background: '#F2F2F2',
                        },
                        ':active': {
                          background: lightTheme.colors.grayScale100,
                        },
                      }}
                      key={index}
                    >
                      <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                        <p
                          style={{
                            ...lightTheme.typography.Small14regular,
                            margin: 0,
                            textTransform: 'uppercase',
                          }}
                        >
                          {t(FORMAT_OPTIONS[opt].title)}
                        </p>
                        <p
                          style={{
                            ...lightTheme.typography.Small11medium,
                            margin: 0,
                            lineHeight: '20px',
                            color: lightTheme.colors.grayScale400,
                          }}
                        >
                          {t(FORMAT_OPTIONS[opt].desc)}
                        </p>
                      </div>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          top: 0,
                          bottom: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {currentFormatValue.id === FORMAT_OPTIONS[opt].id && <Icons.CheckIcon fill={lightTheme.colors.primary} />}
                        {FORMAT_OPTIONS[opt].isPremium && !hasPremium && (
                          <Icons.PremiumIcon/>
                        )}
                      </div>
                    </Block>
                  ))}
                </AutoScroll>
              )}
              onOpen={() => setIsOpenSaveFormat(true)}
              onClose={() => setIsOpenSaveFormat(false)}
            >
              {isOpenSaveFormat ? (
                  <div>
                    <ButtonCustom
                      endEnhancer={<Icons.Drop />}
                      type={SizeButton.LARGE}
                      kind={KIND.tertiary}
                      style={{
                        ...lightTheme.typography.Small14regular,
                        fontWeight: 400,
                        justifyContent: 'space-between',
                        background: '#fff!important',
                        borderColor: '#B2B2B2!important',
                        textTransform: 'uppercase',
                      }}
                    >
                      {currentFormatValue.title}
                    </ButtonCustom>
                  </div>
                ) : (
                  <ButtonCustom
                    endEnhancer={<Icons.Drop />}
                    type={SizeButton.LARGE}
                    kind={KIND.tertiary}
                    style={{
                      ...lightTheme.typography.Small14regular,
                      fontWeight: 400,
                      justifyContent: 'space-between',
                      ':hover': {
                        background: '#fff!important',
                        borderColor: '#B2B2B2!important',
                      },
                      ':active': {
                        background: '#fff!important',
                        borderColor: '#B2B2B2!important',
                      },
                      textTransform: 'uppercase',
                    }}
                  >
                    {currentFormatValue.title}
                  </ButtonCustom>
                )}
            </StatefulPopover>
            {/*  */}
          </div>
          </AutoScroll>
          <div
            style={{
              padding: `${DOWNLOAD_LAYOUT.blockGap}px ${DOWNLOAD_LAYOUT.contentPaddingX}px ${DOWNLOAD_LAYOUT.footerPadding}px`,
              position: window.innerHeight >= DOWNLOAD_LAYOUT.stickyViewportHeight ? 'relative' : 'sticky',
              ...(window.innerHeight >= DOWNLOAD_LAYOUT.stickyViewportHeight
                ? {
                    boxShadow: 'none',
                  }
                : {
                    bottom: 0,
                    left: 0,
                    right: 0,
                    boxShadow: `0px 4px 12px 0px ${lightTheme.colors.primaryBoxShadow}`,
                  }),
              background: lightTheme.colors.white,
              borderRadius: `0 0 ${DOWNLOAD_LAYOUT.modalRadius}px ${DOWNLOAD_LAYOUT.modalRadius}px`,
            }}
          >
            {!hasPremium ? (
              <ButtonCustom
                type={SizeButton.LARGE}
                kind={KIND.secondary}
                startEnhancer={<Icons.PremiumIcon />}
                onClick={() => {
                  dispatch(
                    setOpenModalTryPremium({
                      isOpen: true,
                      source: 'BtSubscriptionWatermark',
                      callback: () => createAndDownloadImage(),
                    })
                  )
                  const eventProperties = buildPremiumPromptEventProperties('BtSubscriptionWatermark')
                  customAmplitude('Premium Prompt', eventProperties)
                }}
              >
                {t('Remove watermark')}
              </ButtonCustom>
            ) : null}
            <ButtonCustom
              type={SizeButton.LARGE}
              kind={KIND.primary}
              startEnhancer={<Download size={24} fill="#fff" />}
              onClick={() => {
                void downloadFromPreviewModal().catch(error => {
                  console.error('Editor download failed:', error)
                  setShowModalCustomText(t('Something went wrong'), t('Something went wrong. Please try again.'))
                })
                close()
              }}
              style={{ marginTop: `${DOWNLOAD_LAYOUT.blockGap}px` }}
            >
              {t('Download')}
            </ButtonCustom>
          </div>
        </div>
      )}
      triggerType="click"
      returnFocus={false}
      autoFocus={false}
      placement="bottomRight"
      popoverMargin={4}
      overrides={{
        Body: {
          style: ({ $theme }) => ({
            zIndex: Z_INDEX.DOWNLOAD_MODAL,
          }),
        },
      }}
      stateReducer={(a, b, c) => {
        setIsOpenPopover(b.isOpen)
        return b
      }}
      dismissOnClickOutside={!(openModalTryPremium.isOpen || showModalType)}
    >
      <div style={{ pointerEvents: isOpenPopover ? 'none' : 'auto', width: '140px', minWidth: 'fit-content' }}>
        <ButtonCustom
          type={SizeButton.STANDARD}
          kind={KIND.primary}
          disabled={disabled}
          onClick={e => {
            // if (hasPremium) {
            //   handleDisplayConfentti()
            //   e.preventDefault()
            //   e.stopPropagation()
            //   createAndDownloadImage()
            // } else {
            //   createAndSetPreviewImage()
            // }
          }}
          style={{
            display: 'flex',
            width: 'fit-content',
            gap: '6px',
            background: disabled
              ? lightTheme.colors.grayScale50
              : isOpenPopover
              ? `${lightTheme.colors.primary100} !important`
              : 'auto',
          }}
        >
          <Download
            size={24}
            fill={disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.white}
          />
          <span
            style={{
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '24px',
              letterSpacing: '-0.006em',
              color: disabled ? '#cccccc' : lightTheme.colors.white,
            }}
          >
            {t('Download')}
          </span>
        </ButtonCustom>
      </div>
    </StatefulPopover>
  )
}

export default ExportImage
